import { Button, RadioCustom } from '@p2p/components';
import { hasProviderQrImage } from '@p2p/entities/qrInvoice/providerQrImage';
import { isSberUzbekistan } from '@p2p/entities/qrInvoice/sber';
import { PaymentCancelModal, usePaymentTemplate } from '@p2p/entities/templates';
import { TGetOrderResponse, TPageSettingsResponse } from '@p2p/services';
import { isCardPaymentLinkOrder, takeCardPaymentRedirect } from '@p2p/shared/functions';
import { routes } from '@p2p/shared/routing';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SELECTED_BANK_STORAGE_KEY } from '../../Payment.constants';
import styles from '../../styles.module.scss';
import { getClientBankId } from './SelectBank.constants';

type SelectBankProps = {
  pageSettings: TPageSettingsResponse;
  order: TGetOrderResponse;
};

export const SelectBank = ({ pageSettings, order }: SelectBankProps) => {
  const { setActiveIndex } = usePaymentTemplate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const banks = useMemo(
    () => [...pageSettings.bank].sort((a, b) => a.sort - b.sort),
    [pageSettings.bank],
  );
  const navigate = useNavigate();
  const isSberUzbekistanOrder = isSberUzbekistan(order);
  const isProviderQrImageOrder = hasProviderQrImage(order);
  // P2P-2915: оплата картой на странице провайдера (SimplePay ecom) — плательщик
  // при каждом открытии сразу уходит туда, без выбора банка и кнопки «Оплатить».
  // Кроме возврата от провайдера по return_url (?from_provider=1): тогда он видит
  // обычную страницу и дожидается статуса (см. takeCardPaymentRedirect). Решение
  // принимается один раз на монтирование.
  const [cardPaymentRedirect] = useState(() =>
    isCardPaymentLinkOrder(order)
      ? takeCardPaymentRedirect(order.id, order.ios_link || order.android_link)
      : null,
  );

  useEffect(() => {
    if (cardPaymentRedirect) {
      // replace, а не assign: наша страница не остаётся в истории, и «Назад» со
      // страницы провайдера ведёт туда, откуда пришёл плательщик, а не по кругу.
      window.location.replace(cardPaymentRedirect);
    }
  }, [cardPaymentRedirect]);

  const handleClick = () => {
    if (!selectedId) return;

    localStorage.setItem(
      SELECTED_BANK_STORAGE_KEY,
      JSON.stringify({ order: order.id, bankId: selectedId }),
    );

    navigate(routes.transferBank.path({ id: order.id, bankId: String(selectedId) }));
  };

  useEffect(() => {
    setActiveIndex(1);
    if (cardPaymentRedirect) {
      return;
    }
    const clientBankId = getClientBankId(order.client_bank_code);

    if (clientBankId !== null) {
      navigate(
        routes.transferBank.path({
          id: order.id,
          bankId: String(clientBankId),
        }),
      );
    }

    if (order.requisite.qr_invoice || isSberUzbekistanOrder || isProviderQrImageOrder) {
      navigate(routes.transferInvoice.path({ id: order.id }));
    }
  }, [
    setActiveIndex,
    order.client_bank_code,
    order.id,
    navigate,
    order.requisite.qr_invoice,
    isSberUzbekistanOrder,
    isProviderQrImageOrder,
    cardPaymentRedirect,
  ]);

  if (cardPaymentRedirect) {
    return null;
  }

  return (
    <div className={styles.transfer_dialog}>
      <div className={classNames(styles.transfer_dialog__title, styles.text_center)}>
        Выберите свой банк
      </div>

      <div className={styles.transfer_issues_list}>
        {banks?.map(({ id, name, svg_image }) => (
          <RadioCustom
            key={id}
            id={id}
            value={name}
            text={name}
            icon={svg_image}
            checked={selectedId === id}
            onChange={() => setSelectedId(id)}
          />
        ))}
      </div>

      <div className={styles.transfer_dialog__actions}>
        <Button onClick={handleClick} disabled={selectedId === null}>
          Продолжить
        </Button>
        <Button onClick={() => setShowCancelModal(true)} variant="outlined">
          Отменить заявку
        </Button>
      </div>
      <PaymentCancelModal
        isActive={showCancelModal}
        setIsActive={setShowCancelModal}
        pageSettings={pageSettings}
        orderId={order.id}
        orderStatus={order.status}
      />
    </div>
  );
};
